import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  slideInLeft,
  slideInRight,
  shouldReduceMotion,
} from '@/lib/animations/optimized-animations';

export interface VsRowProps {
  /** The feature being compared. */
  feature: string;
  /** Whether typical AI ("them") supports this feature. */
  them: boolean;
  /** Whether bunker.ai ("us") supports this feature. */
  us: boolean;
  /** Optional class names merged onto the row container. */
  className?: string;
}

/**
 * A single comparison-table row: a feature label, the "them" (typical AI) side
 * in red, and the bunker.ai side in cyan. The two value cells reveal from
 * opposite edges, gated on reduced-motion (static when reduced).
 */
export function VsRow({ feature, them, us, className }: VsRowProps) {
  const reduce = shouldReduceMotion();

  // Static fallback: render fully visible with no transform when reduced.
  const themMotion = reduce
    ? { initial: false as const, animate: { opacity: 1 } }
    : slideInLeft;
  const usMotion = reduce
    ? { initial: false as const, animate: { opacity: 1 } }
    : slideInRight;

  return (
    <div
      role="row"
      className={clsx(
        'grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-[var(--line)] px-4 py-3',
        className,
      )}
    >
      <span role="cell" className="text-sm font-medium text-[var(--soft)]">
        {feature}
      </span>

      <motion.span
        role="cell"
        className="flex items-center justify-center"
        {...themMotion}
      >
        {them ? (
          <Check aria-label="supported" size={18} className="text-[var(--muted-2)]" />
        ) : (
          <X aria-label="not supported" size={18} className="text-[var(--red)]" />
        )}
      </motion.span>

      <motion.span
        role="cell"
        className="flex items-center justify-center"
        {...usMotion}
      >
        {us ? (
          <Check aria-label="supported" size={18} className="text-[var(--cyan)]" />
        ) : (
          <X aria-label="not supported" size={18} className="text-[var(--muted-2)]" />
        )}
      </motion.span>
    </div>
  );
}

export default VsRow;
